// src/lib/services/data/MessageRouter.ts
// Validates and routes WebSocket messages to DataSyncService.
// Called from websocket.actions.svelte.ts handler functions.

import { dataSyncService } from './DataSyncService';
import { createLogger } from '$lib/utils/logger';
import type {
	DataDeltaMessage,
	DataDeltaBatchMessage,
	DataSnapshotMessage
} from '$lib/types/websocket/messages.types';

const log = createLogger('MessageRouter');

// ---------------------------------------------------------------------------
// routeDataSnapshot
// ---------------------------------------------------------------------------

/**
 * Route a DATA_SNAPSHOT message to DataSyncService.
 *
 * Extracts `entity` and `data` from the message.  When entity is absent the
 * backend is sending a combined "all" snapshot (alerts + work orders in one).
 */
export function routeDataSnapshot(message: DataSnapshotMessage): void {
	const entity = message.entity || 'all';
	const data = message.data;

	if (!data) {
		log.warn('routeDataSnapshot: no data in message', { entity });
		return;
	}

	// Extract data health status from message (backend sets this when data is degraded)
	const dataStatus = message.data_status || 'fresh';

	dataSyncService.handleSnapshot(entity, data as unknown as Parameters<typeof dataSyncService.handleSnapshot>[1], dataStatus);
}

// ---------------------------------------------------------------------------
// routeDataDelta
// ---------------------------------------------------------------------------

/**
 * Route a DATA_DELTA message to DataSyncService.
 *
 * Supports bulk remove when `ids` is provided.
 */
export function routeDataDelta(message: DataDeltaMessage): void {
	const { entity, action, data, id, ids } = message;

	if (!entity || !action) {
		log.warn('routeDataDelta: missing entity or action', { entity, action });
		return;
	}

	// Bulk remove: iterate each id
	if (action === 'remove' && ids && ids.length > 0) {
		for (const removeId of ids) {
			dataSyncService.handleDelta(entity, action, { id: removeId });
		}
		return;
	}

	// Single-item delta
	dataSyncService.handleDelta(entity, action, data ?? { id });
}

// ---------------------------------------------------------------------------
// routeDataDeltaBatch
// ---------------------------------------------------------------------------

/**
 * Route a DATA_DELTA_BATCH message to DataSyncService.
 *
 * Groups items by entity+action for logging, but calls handleDelta per item
 * so DataSyncService can apply each change individually.
 */
export function routeDataDeltaBatch(message: DataDeltaBatchMessage): void {
	const { items } = message;

	if (!items || items.length === 0) {
		log.warn('routeDataDeltaBatch: no items in message');
		return;
	}

	// Group for logging purposes
	const groupCounts = new Map<string, number>();

	for (const item of items) {
		const { entity, action, data, id } = item;

		if (!entity || !action) {
			log.warn('routeDataDeltaBatch: item missing entity or action', { entity, action });
			continue;
		}

		const key = `${entity}:${action}`;
		groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1);

		dataSyncService.handleDelta(entity, action, data ?? { id });
	}

	log.debug('routeDataDeltaBatch processed', {
		totalItems: items.length,
		groups: Object.fromEntries(groupCounts)
	});
}

// ---------------------------------------------------------------------------
// routeDataTick
// ---------------------------------------------------------------------------

/**
 * Route a DATA_TICK message to DataSyncService.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function routeDataTick(message: any): void {
	dataSyncService.handleTick(message);
}
